import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo, ArrowRight, Sparkles, GraduationCap, Stethoscope, Brain, Shield, Eye, FileText, Phone, Check, Users } from '../components/Icons.jsx';
import kunalPhoto from '../assets/kunal.png';
import SevenReasons from '../components/SevenReasons.jsx';

export default function ForSchools() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ school: '', name: '', email: '', cohort: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.school) return;
    setSubmitting(true);
    try {
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'school-enquiry',
          message: `School: ${form.school}\nContact: ${form.name} (${form.email})\nCohort size: ${form.cohort}\nNotes: ${form.notes}`,
          path: '/for-schools'
        })
      });
      await fetch('/api/mailchimp-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, source: 'for-schools-lp', firstName: form.name.split(' ')[0] })
      }).catch(() => {});
      setSubmitted(true);
    } catch {}
    setSubmitting(false);
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--border)',
        padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10
      }}>
        <Link to="/for-schools" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo width="36" height="36" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 4 }}>For schools · powered by Medic Mind</span>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/teachers" className="btn btn-secondary">Teachers portal →</Link>
          <a href="#enquire" className="btn btn-primary">Onboard your school <ArrowRight width="16" height="16" /></a>
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(24px, 4vw, 40px)' }}>
        <section style={{
          background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
          color: '#fff', borderRadius: 20, padding: 'clamp(28px, 5vw, 48px)', marginBottom: 28
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.85, marginBottom: 10 }}>For sixth-form leads, UCAS advisors and SENCos</div>
          <h1 style={{ margin: '0 0 12px', fontSize: 'clamp(28px, 5vw, 44px)', lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Give every medic and dentist in your sixth form the same shot as the privately-tutored ones.
          </h1>
          <p style={{ margin: '0 0 22px', fontSize: 'clamp(15px, 1.8vw, 17px)', color: '#e9d5ff', maxWidth: 720, lineHeight: 1.55 }}>
            Free for schools. Built by Dr Kunal — <strong>scored 100% in UCAT</strong>, personally trained every Medic Mind tutor, and mentored <strong>60,000+ applicants</strong> to doctors over 10 years. Onboard your cohort in 5 minutes.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a href="#enquire" className="btn" style={{ background: '#fff', color: 'var(--brand-deep)' }}>Onboard your school <ArrowRight width="16" height="16" /></a>
            <Link to="/about" className="btn" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>Read founder's letter</Link>
          </div>
        </section>

        <div className="r-grid-3" style={{ marginBottom: 28 }}>
          <Stat value="60,000+" label="students prepped by Medic Mind, now doctors and dentists" />
          <Stat value="10 yrs" label="longest-standing UK medical-applications coach in our specialism" />
          <Stat value="100%" label="UCAT scored by the founder — section playbooks baked into the drills" />
        </div>

        <h2 style={{ margin: '0 0 14px', fontSize: 24 }}>What you get</h2>
        <div className="r-grid-2" style={{ marginBottom: 28 }}>
          <Feature icon={<FileText />} title="13 free drills with difficulty levels" body="Reading Lab, Venn Architect, Mental Arithmetic, Decision Profiler and 9 more. Each with Easy / Medium / Hard, UCATSEN time bonus and per-question rationale." />
          <Feature icon={<Eye />} title="17 free UCAT videos" body="Curated teaching videos from the Medic Mind YouTube channel. VR, DM, QR and SJT — embed straight in lessons." />
          <Feature icon={<Sparkles />} title="Ash — AI tutor for your students" body="Free 20 messages per student per day. Trained on UCAT strategy, GAMSAT, school choice, personal statement, GMC duties and GDC principles." />
          <Feature icon={<Stethoscope />} title="Medic Mind Career Camp" body="In-person NHS hospital + hospice placements in London, Birmingham and Manchester. School groups get priority cohorts. Bursaries up to 100% for widening-participation students." />
          <Feature icon={<GraduationCap />} title="School-internal dashboard (coming soon)" body="Track which of your medics and dentists are using the portal, where they're plateauing, and surface the ones who'd benefit from 1-to-1 support." />
          <Feature icon={<Shield />} title="Tutor portal for staff" body="Live lesson tools designed for projector and Zoom — reveal-on-click scenarios, randomised content per session, facilitator notes only the teacher sees." />
        </div>

        <h2 style={{ margin: '0 0 14px', fontSize: 24 }}>How onboarding works</h2>
        <div className="r-grid-3" style={{ marginBottom: 28 }}>
          <Step n="1" title="Send the form below" body="School name, your contact details, rough cohort size. We reply within 1 working day." />
          <Step n="2" title="Bulk-add your cohort" body="We generate sign-up links you can send round in a single email. Students claim their free accounts in 30 seconds." />
          <Step n="3" title="School-partnership call" body="15-min Zoom to walk through the platform, set up your reporting view, and discuss Career Camp / in-person crash-course options for your cohort." />
        </div>

        <SevenReasons />

        <section className="card" style={{ marginBottom: 28, borderLeft: '5px solid var(--brand-teal)' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>Free for schools. Always.</h2>
          <p style={{ margin: 0, color: 'var(--text-2)' }}>
            The student-facing portal is and will remain free. School partnerships are also free. Premium options exist (Career Camp cohort places, in-school UCAT crash-course visits, 1-to-1 tutoring) — discounted for partner schools, with bursaries up to 100% for widening-participation students. No catches, no per-seat licensing.
          </p>
        </section>

        <section id="enquire" className="card" style={{ marginBottom: 28, scrollMarginTop: 80 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>Onboard your school</h2>
          <p style={{ margin: '0 0 18px', color: 'var(--text-2)' }}>One-minute form. We reply within a working day.</p>

          {submitted ? (
            <div style={{ padding: '20px 22px', background: '#e7f7ef', border: '1px solid rgba(16, 185, 129, 0.35)', borderRadius: 12, color: 'var(--live-green)' }}>
              <Check width="22" height="22" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
              <strong>Thanks — we'll be in touch shortly.</strong> In the meantime, share <Link to="/teachers" style={{ color: 'var(--brand-purple)', fontWeight: 600 }}>the Teachers portal</Link> with the rest of your sixth-form team.
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="r-grid-2" style={{ gap: 12 }}>
                <Field label="School name" value={form.school} onChange={(v) => setForm({ ...form, school: v })} required />
                <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <Field label="Approx. medic/dent cohort size" value={form.cohort} onChange={(v) => setForm({ ...form, cohort: v })} placeholder="e.g. 12" />
              </div>
              <Field
                label="Anything specific you're looking for? (optional)"
                value={form.notes} onChange={(v) => setForm({ ...form, notes: v })}
                textarea
              />
              <button type="submit" disabled={submitting || !form.school || !form.name || !form.email} className="btn btn-primary" style={{ marginTop: 8, opacity: submitting ? 0.5 : 1 }}>
                {submitting ? 'Sending…' : 'Send enquiry'} <ArrowRight width="14" height="14" />
              </button>
              <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>We'll add your email to occasional Medic Mind school-updates. Unsubscribe anytime.</p>
            </form>
          )}
        </section>

        <section className="card" style={{ marginBottom: 28, background: 'var(--card-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <img
              src={kunalPhoto}
              alt="Dr Kunal" width="72" height="72"
              style={{ borderRadius: '50%', flexShrink: 0, objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 4px 16px rgba(35, 20, 63, 0.2)' }}
            />
            <div style={{ flex: 1, minWidth: 240 }}>
              <strong style={{ fontSize: 15 }}>"If you're stuck, or panicked, or thinking your students aren't smart enough — they almost certainly are. They just need the process."</strong>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>Dr Kunal · Founder, Medic Mind · Scored 100% in UCAT · <Link to="/about" style={{ color: 'var(--accent)', fontWeight: 600 }}>read the full letter →</Link></div>
            </div>
          </div>
        </section>

        <footer style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
          © {new Date().getFullYear()} Medic Mind. UCAT is a registered trademark of UCAT Consortium. We are not affiliated with the consortium.
        </footer>
      </main>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)' }}>{value}</div>
      <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--text-2)' }} dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="parents-card">
      <div className="icon-wrap">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="parents-card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--brand-purple)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', fontSize: 17, marginBottom: 10 }}>{n}</div>
      <h3 style={{ fontSize: 16 }}>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, required, textarea }) {
  const common = {
    value, onChange: (e) => onChange(e.target.value),
    placeholder, required,
    style: { width: '100%', padding: '11px 13px', fontSize: 14.5, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 4, fontFamily: 'inherit' }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{label}{required && <span style={{ color: 'var(--brand-purple)' }}> *</span>}</label>
      {textarea ? <textarea rows={3} {...common} style={{ ...common.style, resize: 'vertical' }} /> : <input type={type} {...common} />}
    </div>
  );
}
